import React, { FC, useEffect, useState } from "react";

import { DropdownChangeEvent } from "primereact/dropdown";
import { InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputSwitchChangeEvent } from "primereact/inputswitch";
import { DashboardEditorProps } from "components/ComponentType";
import SparkLineAdvancedInputs from "../sparkline-advanced/SparkLineAdvancedInputs";
import SparkLineInputs from "./SparkLineInputs";
import { SparkLineProps } from "./SparkLineTypes";

const defaultSparkLineConfig: SparkLineProps = {
  valueScaling: 1,
  decimalPlaces: 0,
  labelFontSize: "2rem",
  sparkLineWidth: 300,
  dataset: null,
  count: 14,
  unit: "",
  overlayLabel: false,
  vertical: "center",
  horizontal: "center",
  updateFrequency: 0,
  lineColor: "#41c3f9",
  lineWidth: 2,
};

const defaultAdvancedSparkLineConfig: Partial<SparkLineProps> = {
  lineColor: "#5C7577",
  baseline: {
    usage: "dataset",
    color: "#ABBCBE",
    fillAboveColor: "#DAE2E3",
    fillBelowColor: "#CED8D9",
    width: 2,
  },
  minLine: {
    usage: "none",
    color: "#ffe48a",
  },
  maxLine: {
    usage: "none",
    color: "#fd9c91",
  },
}

const cleanConfig = (config: SparkLineProps): SparkLineProps => {
  const {
    constant,
    baselineDataset,
    baselineColor,
    baselineWidth,
    fillAboveColor,
    fillBelowColor,
    showMinMax,
    showCurrent,
    useConstant,
    ...rest
  } = config as any; // clean legacy config properties

  const line = useConstant ? { constant } : { dataset: baselineDataset}
  return {
    ...rest,
    baseline: {
      ...line,
      usage: useConstant ? "constant" : "dataset",
      color: baselineColor,
      width: baselineWidth,
      fillAboveColor,
      fillBelowColor,
      ...config.baseline
    },
    minLine: {
      color: defaultAdvancedSparkLineConfig.minLine?.color,
      usage: "none",
      ...config.minLine
    },
    maxLine: {
      usage: "none",
      color: defaultAdvancedSparkLineConfig.maxLine?.color,
      ...config.maxLine
    },
  };
}

interface SparkLineEditorProps extends DashboardEditorProps<SparkLineProps> {
  advanced?: boolean;
}

const SparkLineEditor: FC<SparkLineEditorProps> = ({ config, onConfigChange, advanced }) => {
  const [currentConfig, setCurrentConfig] = useState(() => {
    if (Object.keys(config).length === 0) {
      return advanced
        ? { ...defaultSparkLineConfig, ...defaultAdvancedSparkLineConfig }
        : defaultSparkLineConfig;
    }
    return cleanConfig(config);
  });

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const setConfigPropertyState = (
    property: keyof SparkLineProps,
    value: string | number | boolean | null
  ) => {
    setCurrentConfig(prevState => ({ ...prevState, [property]: value }));
  };

  const handleConfigChange = (
    e:
      | InputNumberValueChangeEvent
      | React.ChangeEvent<HTMLInputElement>
      | InputSwitchChangeEvent
      | DropdownChangeEvent
  ): void => {
    setConfigPropertyState(e.target.id as keyof SparkLineProps, e.target.value);
  };

  return (
    <div className="flex flex-wrap gap-2">
      <SparkLineInputs
        currentConfig={currentConfig}
        onDatasetChange={setConfigPropertyState}
        onConfigChange={handleConfigChange}
        advanced={advanced}
      />
      {advanced && (
        <SparkLineAdvancedInputs currentConfig={currentConfig} onDatasetChange={setCurrentConfig} />
      )}
    </div>
  );
};

export default SparkLineEditor;
